import { Component, lazy, Suspense } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ToastProvider } from '@components/ui/Feedback';
import { Toaster } from 'react-hot-toast';
import { store } from '@store/store';
import AppRouter from '@routes/AppRouter';
import { SocketProvider } from './contexts/SocketProvider';

// Desktop integration (safe no-op on web)
const DesktopNotificationBridge = lazy(() => import('@components/desktop/DesktopNotificationBridge'));
const DesktopTitleBar = lazy(() => import('@components/desktop/DesktopTitleBar'));
const isDesktopApp = !!window.zentrixDesktop;

// ═══════════════════════════════════════════
//  ERROR BOUNDARY
// ═══════════════════════════════════════════
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Zentrix Error Boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-surface-50 p-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-danger-50 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#F44336" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-surface-900 mb-2">Something went wrong</h1>
            <p className="text-sm text-surface-600 mb-6">
              An unexpected error occurred. Please refresh the page or contact support if the issue persists.
            </p>
            <button
              onClick={() => { this.setState({ hasError: false, error: null }); window.location.href = '/'; }}
              className="px-6 py-2.5 bg-primary-900 text-white font-semibold rounded-xl hover:bg-primary-800 transition-colors"
            >
              Refresh & go home
            </button>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-6 text-left text-xs text-danger-600 bg-danger-50 rounded-xl p-4 overflow-auto max-h-48">
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ═══════════════════════════════════════════
//  APP ROOT
// ═══════════════════════════════════════════
const App = () => {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <BrowserRouter>
          <SocketProvider>
          {/* Desktop-only: custom title bar + native notification bridge */}
          {isDesktopApp && (
            <Suspense fallback={null}>
              <DesktopTitleBar />
              <DesktopNotificationBridge />
            </Suspense>
          )}
          <AppRouter />

          {/* Single toast provider — used by all module pages */}
          <ToastProvider position="top-right" />
          {/* react-hot-toast renderer — many components (integrations, admin,
              projects, auth) call react-hot-toast directly, but its <Toaster>
              was only mounted in the client-portal and super-admin layouts,
              so every one of those toasts silently no-opped in the main app. */}
          <Toaster
            position="top-right"
            toastOptions={{ style: { background: '#ffffff', color: '#1e293b', border: '1px solid #e2e8f0', fontSize: '13px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}
          />
          </SocketProvider>
        </BrowserRouter>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;